import { useEffect, useCallback } from 'react'
import { Socket } from 'socket.io-client'
import { useSocketContext } from '../context/SocketContext'
import { EventSchema, EventNames, ConnectionStatus } from '../types'

/**
 * Hook to access the socket manager and connection status
 *
 * @param namespace - Optional namespace (default: '/')
 *
 * @example
 * // Use root namespace (for OCR)
 * const { emit, isConnected } = useSocket()
 *
 * @example
 * // Use notifications namespace
 * const { emit, isConnected } = useSocket('/notifications')
 */
export const useSocket = (
    namespace: string = '/',
): {
    status: ConnectionStatus
    isConnected: boolean
    emit: <T extends EventNames>(event: T, payload: EventSchema[T]) => void
    socket: Socket | null
} => {
    const { socketManager, getStatus, isNamespaceConnected } = useSocketContext()

    const emit = useCallback(
        <T extends EventNames>(event: T, payload: EventSchema[T]) => {
            socketManager.emit(namespace, event, payload)
        },
        [socketManager, namespace],
    )

    return {
        status: getStatus(namespace),
        isConnected: isNamespaceConnected(namespace),
        emit,
        socket: socketManager.getSocket(namespace),
    }
}

/**
 * Hook to listen for a specific socket event with automatic cleanup
 *
 * @param event - Event name to listen for
 * @param handler - Handler function
 * @param dependencies - React dependencies for handler
 * @param namespace - Optional namespace (default: '/')
 *
 * @example
 * // Listen on root namespace (for OCR)
 * useSocketEvent('ocr_update', (payload) => { ... })
 *
 * @example
 * // Listen on notifications namespace
 * useSocketEvent('notification', (payload) => { ... }, [], '/notifications')
 */
export const useSocketEvent = <T extends EventNames>(
    event: T,
    handler: (payload: EventSchema[T]) => void,
    dependencies: any[] = [],
    namespace: string = '/',
): void => {
    const { socketManager, isNamespaceConnected } = useSocketContext()

    useEffect(() => {
        const socket = socketManager.getSocket(namespace)
        if (!socket) return

        socket.on(event as string, handler)

        return () => {
            socket.off(event as string, handler)
        }
    }, [event, socketManager, handler, namespace, isNamespaceConnected(namespace), ...dependencies])
}
